import { type ReactNode } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  rectSortingStrategy,
} from "@dnd-kit/sortable";

export interface DraggableListProps {
  /** Stable IDs of the items in their current display order. */
  itemIds: string[];
  /** Called when user drops an item — receives the indices to swap. */
  onReorder: (oldIndex: number, newIndex: number) => void;
  /** Layout hint for @dnd-kit's hit-testing strategy. Default: "vertical". */
  strategy?: "vertical" | "grid";
  /** Render children inside DndContext + SortableContext (your sortable rows or grid cells). */
  children: ReactNode;
}

/**
 * Wraps @dnd-kit's DndContext + SortableContext + sensor setup so consumers don't have to
 * repeat the boilerplate. Consumers still own the `useSortable` per-item rendering and the
 * actual reorder mutation; this component handles only the orchestration.
 */
export default function DraggableList({
  itemIds,
  onReorder,
  strategy = "vertical",
  children,
}: DraggableListProps) {
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = itemIds.indexOf(active.id as string);
    const newIndex = itemIds.indexOf(over.id as string);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder(oldIndex, newIndex);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={itemIds}
        strategy={strategy === "grid" ? rectSortingStrategy : verticalListSortingStrategy}
      >
        {children}
      </SortableContext>
    </DndContext>
  );
}
